import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';


import UserTable from './components/UserTable';
import UserDetailModal from './components/UserDetailModal';
import CreateUserModal from './components/CreateUserModal';
import BulkActionsBar from './components/BulkActionsBar';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const [users, setUsers] = useState([
    {
      id: 1,
      name: 'Dr. Sarah Mitchell',
      email: 'sarah.mitchell@example.com',
      role: 'Provider',
      practice: 'Mitchell Orthopedics',
      location: 'Los Angeles, CA',
      registrationDate: '2025-12-15',
      status: 'Active',
      lastLogin: '2026-01-22'
    },
    {
      id: 2,
      name: 'James Wilson',
      email: 'james.wilson@example.com',
      role: 'Practice Marketer',
      practice: 'Wilson Medical Group',
      location: 'New York, NY',
      registrationDate: '2025-11-20',
      status: 'Active',
      lastLogin: '2026-01-23'
    },
    {
      id: 3,
      name: 'Dr. Emily Chen',
      email: 'emily.chen@example.com',
      role: 'Provider',
      practice: 'Chen Surgical Center',
      location: 'San Francisco, CA',
      registrationDate: '2026-01-05',
      status: 'Pending',
      lastLogin: 'Never'
    },
    {
      id: 4,
      name: 'Michael Rodriguez',
      email: 'michael.r@example.com',
      role: 'Administrator',
      practice: 'Videra Surgical',
      location: 'Boston, MA',
      registrationDate: '2025-10-01',
      status: 'Active',
      lastLogin: '2026-01-23'
    },
    {
      id: 5,
      name: 'Dr. Amanda Foster',
      email: 'amanda.foster@example.com',
      role: 'Provider',
      practice: 'Foster Orthopedic Clinic',
      location: 'Chicago, IL',
      registrationDate: '2025-12-28',
      status: 'Active',
      lastLogin: '2026-01-21'
    },
    {
      id: 6,
      name: 'David Kim',
      email: 'david.kim@example.com',
      role: 'Practice Marketer',
      practice: 'Kim Healthcare Marketing',
      location: 'Seattle, WA',
      registrationDate: '2026-01-10',
      status: 'Suspended',
      lastLogin: '2026-01-15'
    },
    {
      id: 7,
      name: 'Dr. Lisa Thompson',
      email: 'lisa.thompson@example.com',
      role: 'Provider',
      practice: 'Thompson Surgical Associates',
      location: 'Miami, FL',
      registrationDate: '2025-11-12',
      status: 'Active',
      lastLogin: '2026-01-22'
    },
    {
      id: 8,
      name: 'Robert Martinez',
      email: 'robert.m@example.com',
      role: 'Practice Marketer',
      practice: 'Martinez Medical Marketing',
      location: 'Dallas, TX',
      registrationDate: '2025-12-03',
      status: 'Active',
      lastLogin: '2026-01-23'
    }
  ]);

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'Provider', label: 'Provider' },
    { value: 'Practice Marketer', label: 'Practice Marketer' },
    { value: 'Administrator', label: 'Administrator' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Active', label: 'Active' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Suspended', label: 'Suspended' }
  ];

  // Filter users based on search and filters
  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.practice?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = !roleFilter || user?.role === roleFilter;
    const matchesStatus = !statusFilter || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers?.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers?.length / usersPerPage);

  const handleUserSelect = (userId) => {
    setSelectedUsers((prev) =>
      prev?.includes(userId) ? prev?.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === currentUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers?.map((user) => user?.id));
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers((prev) => prev?.filter((user) => user?.id !== userId));
    }
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on users:`, selectedUsers);
    setSelectedUsers([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60">
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">User Management</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                Manage platform users, roles, and permissions
              </p>
            </div>
            <UserAccountMenu />
          </div>
        </header>

        <main className="p-4 md:p-6 space-y-6">
          {/* Filters and Search */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Input
                  type="search"
                  placeholder="Search by name, email, or practice..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
              </div>
              <Select
                options={roleOptions}
                value={roleFilter}
                onChange={setRoleFilter}
                placeholder="Filter by role"
              />
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="Filter by status"
              />
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {currentUsers?.length} of {filteredUsers?.length} users
              </p>
              <Button
                variant="default"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => setShowCreateUser(true)}
              >
                Create New User
              </Button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedUsers?.length > 0 && (
            <BulkActionsBar
              selectedCount={selectedUsers?.length}
              onAction={handleBulkAction}
              onClear={() => setSelectedUsers([])}
            />
          )}

          {/* User Table */}
          <UserTable
            users={currentUsers}
            selectedUsers={selectedUsers}
            onSelectUser={handleUserSelect}
            onSelectAll={handleSelectAll}
            onViewUser={handleViewUser}
            onDeleteUser={handleDeleteUser}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                iconName="ChevronLeft"
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-4">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      {showUserDetail && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setShowUserDetail(false)}
          onUpdate={(updatedUser) => {
            setUsers((prev) =>
              prev?.map((u) => (u?.id === updatedUser?.id ? updatedUser : u))
            );
            setShowUserDetail(false);
          }}
        />
      )}

      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreate={(newUser) => {
            setUsers((prev) => [...prev, { ...newUser, id: Date.now() }]);
            setShowCreateUser(false);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;